import { createSelector } from '@reduxjs/toolkit';
import { getFirstName } from 'common/utils';
import { EventRoomAttendeeBasicInfo } from 'domain/event';
import type { RootState } from 'store';
import { selectUserAccount } from 'user/api';

import { answersAdapter } from './answers.slice';
import { RoomQuestionAnswerAuthor } from './types';

const answersSelectors = answersAdapter.getSelectors<RootState>((state) => state.question.answers);

const selectQuestionsRoot = (state: RootState) => state.question;

export const selectQuestionTitle = createSelector(selectQuestionsRoot, (state) => state.questionAssignment?.title);
export const selectQuestionActiveAnswerId = (state: RootState) => state.question.questionAssignment.activeAnswerId;
export const selectQuestionAssignment = createSelector(selectQuestionsRoot, (state) => state.questionAssignment);
export const selectAnswers = (state: RootState) => answersSelectors.selectAll(state);
export const selectAnswersIds = (state: RootState) => answersSelectors.selectIds(state);
export const selectAnswer = answersSelectors.selectById;

export const selectAnswerUpdatedByUserId = createSelector(selectAnswer, (answer) => answer?.updatedByUserId);

export const selectAnswerIsUpdatedByCurrentUser = createSelector(
  selectAnswerUpdatedByUserId,
  selectUserAccount,
  (updatedByUserId, user) => updatedByUserId === user?.id
);

export const selectAnswerAssignedToUserIds = createSelector(selectAnswer, (answer) => answer?.assignedToUserIds);

export const selectAnswerText = createSelector(selectAnswer, (answer) => answer?.text);

export const selectAnswerAuthor = createSelector(selectAnswer, selectUserAccount, (answer, user) => {
  if (!answer) return RoomQuestionAnswerAuthor.Unknown;

  if (answer.isAnonymous) return RoomQuestionAnswerAuthor.Anonymous;
  if (answer.assignedToUserIds.length > 1) return RoomQuestionAnswerAuthor.Shared;
  if (answer.assignedToUserIds.every((id) => id === user?.id)) return RoomQuestionAnswerAuthor.Me;
  return RoomQuestionAnswerAuthor.Participant;
});

export const selectIsSharedAnswer = createSelector(
  selectAnswerAuthor,
  (author) => author === RoomQuestionAnswerAuthor.Shared
);

export const selectAnswerEditedByLabel = createSelector(
  selectAnswer,
  selectUserAccount,
  (state: RootState, answerId: string, attendees: EventRoomAttendeeBasicInfo[]) => attendees,
  (answer, user, attendees) => {
    if (!answer?.updatedByUserId) return;

    const updatedBy = attendees.find(({ id }) => id === answer.updatedByUserId && id !== user?.id);
    if (updatedBy) return `${getFirstName(updatedBy)} is editing...`;
  }
);

export const selectAnswerCanEdit = createSelector(
  (state: RootState, answerId?: string) => (answerId ? selectAnswer(state, answerId) : undefined),
  selectUserAccount,
  selectAnswerIsUpdatedByCurrentUser,
  (answer, user, isUpdatedByCurrentUser) => {
    if (!user?.id || !answer) return false;
    return (!!isUpdatedByCurrentUser && !answer.assignedToUserIds.length) || answer.assignedToUserIds.includes(user.id);
  }
);
